import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import ChatConversation from "@/components/ChatConversation";
import { Button } from "@/components/ui/button";
import PagedList from "@/components/PagedList";
import { PlusIcon, SparkleIcon, WarningIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ChatPage() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hasKey, setHasKey] = useState(true); // assume yes until checked, avoids a flash of the banner

  const load = async () => {
    const { data } = await api.get("/chat/conversations");
    setConvs(data);
    if (!selected && data[0]) setSelected(data[0]);
  };
  const loadKeyStatus = async () => {
    try {
      const { data } = await api.get("/account/ai-key");
      // An org-wide key (set once by an admin) covers everyone by default;
      // a personal key just overrides it for that one user.
      setHasKey(!!data.has_key || !!data.org_key_configured);
    } catch (e) { /* non-fatal — worst case the send button surfaces the error */ }
  };
  useEffect(() => { load(); loadKeyStatus(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/chat/conversations", { title: "New chat" });
      setConvs([data, ...convs]);
      setSelected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">AI</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Ask AI</h1>
        </div>
        <div className="text-xs font-mono text-zinc-500 flex items-center gap-2">
          <SparkleIcon size={12} weight="fill" className="text-[#0176D3]" />
          Anthropic Claude Sonnet 4.5 · grounded in your workspace
        </div>
      </div>

      {!hasKey && (
        <div className="mx-8 mt-4 flex items-center gap-2 text-sm px-4 py-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-800" data-testid="no-api-key-banner">
          <WarningIcon size={16} weight="fill" />
          Ask AI isn't set up yet — ask an admin to add an organization key, or{" "}
          <Link to="/account/password" className="font-medium underline hover:text-amber-900">add your own personal key</Link>.
        </div>
      )}

      <div className="px-8 py-6 grid lg:grid-cols-[280px_1fr] gap-6 trk-fade-in h-[calc(100vh-140px)]">
        <div className="border border-border rounded-sm flex flex-col">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <div className="trk-uppercase-label">Conversations</div>
            <button onClick={create} data-testid="new-chat" className="p-1 border border-border rounded-sm hover:bg-zinc-50">
              <PlusIcon size={12} weight="bold" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <PagedList items={convs} pageSize={25} testIdPrefix="convs" className="px-3 pb-2">
              {(rows) => (
                <div className="-mx-3">
            {rows.map((c) => (
              <div
                key={c._id}
                className={`group flex items-center justify-between px-3 py-2 border-b border-border last:border-0 hover:bg-zinc-50 cursor-pointer ${selected?._id === c._id ? "bg-[#EFF6FF] border-l-2 border-l-[#0176D3]" : ""}`}
                onClick={() => setSelected(c)}
                data-testid={`conv-${c._id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{c.title}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{new Date(c.updated_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
                </div>
              )}
            </PagedList>
            {convs.length === 0 && (
              <div className="p-6 text-center">
                <div className="text-sm text-zinc-500 mb-3">No conversations yet</div>
                <Button onClick={create} className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]" data-testid="start-chat">
                  <PlusIcon size={12} weight="bold" className="mr-1" /> Start chat
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="border border-border rounded-sm bg-zinc-50/30 min-h-0">
          {selected ? (
            <ChatConversation conversation={selected} onTitleChange={load} />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-zinc-500">
              Create a new conversation to begin.
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
